import { ensureRouteData } from '@/app/query-client'
import { courseQuestionsQueryOptions } from '@/features/courses/api/queries'
import type { RouterContext } from '@/routes/layouts/root'
import type { PageQuery } from '@/schemas/courses'

/** How many questions the list opens with, and how many each "Load more" adds. */
export const FAQ_STEP = 10

/** mm-be's ceiling on `page_size` (`api/v5/courses/serializers.py`): a longer ask is a 400. */
export const FAQ_MAX = 200

/**
 * The questions read for a list of `limit` questions: always page 1, at a size
 * that grows. "Load more" re-reads the same questions plus the next few rather
 * than stacking pages, so the list is one cache entry per length and the
 * count under it is the server's own total for exactly that read. Built here
 * once, for the loader and the screen alike. `limit` is clamped to `FAQ_MAX`, the
 * most one read can carry.
 */
export const faqQuery = (limit: number): PageQuery => ({
  page: 1,
  pageSize: Math.min(limit, FAQ_MAX),
})

/**
 * The `/courses/faq` loader.
 *
 * MAIN: the opening questions. The screen reads them with `useCourseQuestions`
 * (`keepPreviousData`), not suspense: a "Load more" keeps the current
 * questions on screen while the longer list arrives (docs/route-loading.md,
 * "Search params").
 */
export async function loadFaq({
  context: { queryClient },
}: {
  context: RouterContext
}): Promise<void> {
  await ensureRouteData(queryClient, courseQuestionsQueryOptions(faqQuery(FAQ_STEP)))
}
